import * as React from 'react';
import debounce from 'debounce';
import { ChangeEventValue, Coords } from 'google-map-react';
import { withStyles, WithStyles, createStyles } from '@material-ui/core/styles';

import GoogleMap from 'ha/modules/GoogleMap';
import { PartnerMarker, RoomMarker } from './SearchMarkers';
import SearchMapAutoRefresh from '../containers/SearchMapAutoRefresh';
import { RoomMarker as RoomMarkerType, UniMarker } from './SearchMarkers/Types';
import { customDistanceToMouse } from '../utils/customDistanceToMouse';

const MAP_MARGIN = [10, 10, 10, 10];
const HOVER_DISTANCE = 30;

interface Props {
  initialized: boolean;
  markers: RoomMarkerType[];
  universities: UniMarker[];
  mapCenter: Coords;
  mapZoom: number;
  onMapChanged: (event: ChangeEventValue) => void;
  highlightedRoomId: number;
  scrollwheel: boolean;
  isMobileMapOpen: boolean;
  isAutoRefreshButtonVisible?: boolean;
  isAutoSearchEnabled?: boolean;
  toggleMapAutoSearch: (isOn: boolean) => void;
}

interface State {
  ignoreOnChange: boolean;
}

const styles = createStyles({
  container: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: '100%',
    transition: 'margin-bottom 0.3s ease-out',
  },
  mapCanvas: {
    width: '100%',
    height: '100%',
  },
});

export class SearchMap extends React.Component<
  Props & WithStyles<typeof styles>,
  State
> {
  static defaultProps = {
    highlightedRoomId: 0,
    isAutoRefreshButtonVisible: true,
  };

  state = {
    ignoreOnChange: true,
  };

  mapContainerRef: React.RefObject<HTMLDivElement> = React.createRef();

  handleBoundsChange = debounce((params: ChangeEventValue) => {
    this.props.onMapChanged(params);
  }, 800);

  componentDidMount() {
    if (this.props.isAutoSearchEnabled) {
      this.props.toggleMapAutoSearch(true);
    }
  }

  handleOnChange = (mapChange: ChangeEventValue) => {
    // We don't want to trigger onChange the first time it gets triggered (on first map render).
    // We want the map boundaries to be relevant for search only when the user interacted with it.
    if (this.state.ignoreOnChange) {
      this.setState({ ignoreOnChange: false });
    } else {
      this.handleBoundsChange(mapChange);
    }
  };

  handleDrag = () => {
    this.handleBoundsChange.clear();
  };

  gestureHandling = () => {
    const { isMobileMapOpen } = this.props;

    if (isMobileMapOpen) {
      return 'greedy';
    } else {
      return 'cooperative';
    }
  };

  render() {
    const {
      classes,
      markers,
      universities,
      mapCenter,
      mapZoom,
      highlightedRoomId,
      scrollwheel,
      isMobileMapOpen,
      isAutoRefreshButtonVisible,
    } = this.props;

    return (
      <div className={classes.container}>
        <div
          id="map-canvas"
          className={classes.mapCanvas}
          ref={this.mapContainerRef}
        >
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            distanceToMouse={customDistanceToMouse}
            options={{
              scrollwheel: isMobileMapOpen ? false : scrollwheel,
              zoomControl: isMobileMapOpen ? false : true,
              fullscreenControl: false,
              gestureHandling: this.gestureHandling(),
            }}
            onChange={this.handleOnChange}
            onDrag={this.handleDrag}
            margin={MAP_MARGIN}
            hoverDistance={HOVER_DISTANCE}
          >
            {universities.map(univer => (
              <PartnerMarker
                key={univer.id}
                lat={univer.latitude}
                lng={univer.longitude}
                marker={univer}
                boundariesElement={this.mapContainerRef.current}
              />
            ))}
            {markers.map(marker => (
              <RoomMarker
                key={marker.id}
                lat={marker.latitude}
                lng={marker.longitude}
                marker={marker}
                highlightedRoomId={highlightedRoomId}
                boundariesElement={this.mapContainerRef.current}
              />
            ))}
          </GoogleMap>
          {isAutoRefreshButtonVisible && <SearchMapAutoRefresh />}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(SearchMap);
